import React, { useState } from "react";
import { useNavigate } from 'react-router-dom';
import BottomNav from "../components/BottomNav";

import Notification from "../components/modal/Notification";

import DoctorBusy from "../components/modal/DoctorBussy";

import '@fortawesome/fontawesome-free/css/all.min.css';
import ConsultationCard from "../components/card/ConsultationCard";
import DoctorCard from "../components/card/DoctorCard";
import ConsultationActionCard from "../components/card/ConsultationActionCard";
import PendingPaymentCard from "../components/card/PendingPaymentCard";
import PendingPaymentModal from "../components/modal/PendingPaymentModal";
import Confirm from "../components/modal/Confirm";
import { cancelPendingPayment, PendingPayment } from "../services/pendingPaymentService";
import Loading from "../components/Loading";
import Service from "../components/modal/Service";
import { useDoctors } from "../hooks/useDoctors";
import { useHome } from "../hooks/useHome";
import { useAuthStore } from '../stores/authStores';
import { useTeleconsultation } from "../hooks/useTeleconsultation";
import { Doctor } from "../types/Doctor";
import { useCoverage } from "../hooks/useCoverage";
import InfoModal from '../components/modal/InfoModal';
import { activeConsultationMessage, activeConsultationRoute, isActiveConsultation } from '../utils/activeConsultation';

const Home: React.FC = () => {
  const navigate = useNavigate();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [showActiveConsultation, setShowActiveConsultation] = useState(false);
  const [isModalOpen, setModalOpen] = useState(false); 
  const [showModal, setShowModal] = useState<boolean>(false);
  const [showModalAppointment, setShowModalAppointment] = useState<boolean>(false);
  const [showService, setShowService] = useState(false);
  const [selectedDoctor, setSelectedDoctor] = useState<any | null>(null);
  const userData = useAuthStore((state) => state.user);
  const token = useAuthStore((state) => state.token);

  const { data: homeData, isLoading, refetch: refetchHome } = useHome(userData?.id, token);
  const pendingPayment: PendingPayment | null = homeData?.pending_payment ?? null;
  const [pendingAction, setPendingAction] = useState<(() => void) | null>(null);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [cancellingPayment, setCancellingPayment] = useState(false);
  const [insuranceNotice, setInsuranceNotice] = useState<string | null>(null);
  const isInsuranceInactive = homeData?.insurance?.is_active === false;

  const openPaymentPage = React.useCallback((teleconsultId: number | string, encToken: string, hasGatewayPayment: boolean, replace = false) => {
    if (hasGatewayPayment) {
      navigate(`/payment-success?teleconsult_id=${teleconsultId}&enc_token=${encToken}`, { replace });
      return;
    }
    navigate(`/checkout-teleconsultation/${teleconsultId}`, { replace });
  }, [navigate]);

  const openConsultation = React.useCallback((t: any, replace = false) => {
    if (!t?.id) return false;
    const encToken = t.encToken || '';

    switch (t.stage) {
      case 'initial':
        navigate(`/connect-doctor/${t.id}/${encToken}?stage=initial`, { replace });
        return true;
      case 'payment':
        openPaymentPage(
          t.id,
          encToken,
          Boolean(pendingPayment?.teleconsult_id === t.id && pendingPayment?.payment_url),
          replace
        );
        return true;
      case 'final-confirm':
        navigate(`/connect-doctor/${t.id}/${encToken}?stage=final-confirm`, { replace });
        return true;
      case 'chat':
        navigate(`/chat/${t.id}/${encToken}`, { replace });
        return true;
      default:
        return false;
    }
  }, [navigate, openPaymentPage, pendingPayment]);

  React.useEffect(() => {
    const resync = () => {
      if (document.visibilityState === 'visible') refetchHome();
    };
    window.addEventListener('pageshow', resync);
    document.addEventListener('visibilitychange', resync);
    return () => {
      window.removeEventListener('pageshow', resync);
      document.removeEventListener('visibilitychange', resync);
    };
  }, [refetchHome]);

  const withPendingPaymentCheck = async (action: () => void) => {
    const fresh = (await refetchHome()).data ?? homeData;

    if (fresh?.insurance?.is_active === false) {
      setInsuranceNotice(fresh.insurance.message || 'Masa asuransi Anda tidak aktif');
      return;
    }
    if (fresh?.pending_payment) {
      setPendingAction(() => action);
      return;
    }
    if (isActiveConsultation(fresh?.teleconsult)) {
      setShowActiveConsultation(true);
      return;
    }
    action();
  };

  const continuePendingPayment = () => {
    if (!pendingPayment) return;
    setPendingAction(null);
    openPaymentPage(
      pendingPayment.teleconsult_id,
      pendingPayment.enc_token || homeData?.teleconsult?.encToken || '',
      Boolean(pendingPayment.payment_url)
    );
  };

  const cancelPayment = async (afterCancel?: () => void) => {
    if (!pendingPayment || !token) return;
    setCancellingPayment(true);
    try {
      await cancelPendingPayment(pendingPayment.teleconsult_id, token);
      await refetchHome();
      afterCancel?.();
    } catch (err: any) {
      setErrorMessage(err.response?.data?.message || "Gagal membatalkan pembayaran");
      await refetchHome();
    } finally {
      setCancellingPayment(false);
      setShowCancelConfirm(false);
      setPendingAction(null);
    }
  };

  React.useEffect(() => {
    const pendingId = sessionStorage.getItem('pending_checkout_id');
    if (!pendingId || !homeData) return;

    sessionStorage.removeItem('pending_checkout_id');
    const t = homeData.teleconsult;
    if (t && String(t.id) === pendingId) {
      openConsultation(t, true);
    }
  }, [homeData, openConsultation]);
  const { data: teleconsultation } = useTeleconsultation(token);
  const { data: doctors } = useDoctors(token);
  const practitioner_role_id = teleconsultation?.id;
  const { data: coverage } = useCoverage(token);
  const isDokterUmum = coverage?.data?.is_dokter_umum ?? true;
  const doctorData = React.useMemo<Doctor[]>(() => {
    if (!doctors) return [];

    return doctors.map((item: any, index: number) => ({
      practitioner_role_id: item.id,
      name: item.doctorName,
      specialization: item.doctorSpeciality || "Umum",
      years: parseInt(item.experience, 10),
      rating: Math.round((item.rating || 0) * 20),
      hospital: item.hospital_name?.replace(/\r?\n|\r/g, "").trim() || "",
      price_chat: item.price_chat,
      price_video: item.price_video,
      photoUrl: item.photoUrl || `https://i.pravatar.cc/150?img=${index + 1}`,
      is_online: item.availabilityStatus === "ONLINE",
      availability: item.availabilityStatus,
      isMyOngoing: item.isMyOngoing,
    }));
  }, [doctors]);

  const getGreeting = () => {
    const hour = new Date().getHours();

    if (hour >= 4 && hour < 12) {
      return { color: 'morning', text: 'Selamat Pagi', image: 'morning' };
    } else if (hour >= 12 && hour < 17) {
      return { color: 'afternoon', text: 'Selamat Siang', image: 'afternoon' };
    } else if (hour >= 17 && hour < 18) {
      return { color: 'evening', text: 'Selamat Sore', image: 'afternoon' };
    } else {
      return {
        color: 'evening',
        text: 'Selamat Malam',
        image: 'morning',
        style: {
          background: 'linear-gradient(180deg, rgba(2,0,36,1) 0%, rgba(14,24,34,1) 35%, rgba(29,88,122,1) 100%)'
        }
      };
    }
  };

  const handleOnClickLivechat = () => {
    if (openConsultation(homeData?.teleconsult)) return;

    withPendingPaymentCheck(() => navigate(`/list-doctor`, {
      state: { filters: { schedule: 1 } }
    }));
  }

  const handleSpecialistClick = (id: number) => {
    navigate('/list-doctor', {
      state: { filters: { speciality_id: id } }
    });
  };


  const greeting = getGreeting();
  return (
    <div style={{ fontFamily: "Arial, sans-serif", backgroundColor: "#D8F3F7", minHeight: "100vh", paddingBottom: "60px", // Tambahkan ini
      }}>
      {/* Header + Greeting Section */}
        <div className="flex flex-wrap justify-center">
          {/* Bagian Kiri */}
          <div className="flex flex-col items-center p-8 pb-0 bg-morning-300 flex-1 w-full md:w-1/2 sm:flex-initial">
            <h2
              className="mb-4 text-xl text-white lg:mb-2 sm:text-2xl md:sm:text-xl"
            >
              {greeting.text}, {userData ? userData.person.name : ""}!
            </h2>
            <img
              className="w-[200px] mx-auto lazy mb-4"
              src='/images/greeting.png'
              alt="Good Selamat Pagi"
            />
            {!isDokterUmum && (
              <div className="mx-4 mt-4 mb-6 p-5 bg-gradient-to-r from-yellow-200 to-yellow-100 border border-yellow-300 rounded-xl shadow">
                <p className="text-yellow-900 text-base font-semibold text-center">
                  Telekonsultasi belum tersedia
                </p>
                <p className="text-yellow-800 text-sm text-center mt-2">
                  Fitur ini belum termasuk dalam manfaat polis Kamu saat ini.
                </p>
              </div>
            )}
            {isInsuranceInactive && (
              <div className="mx-4 mt-4 mb-6 p-5 bg-gradient-to-r from-red-200 to-red-100 border border-red-300 rounded-xl shadow">
                <p className="text-red-900 text-base font-semibold text-center">
                  {homeData.insurance.message}
                </p>
                <p className="text-red-800 text-sm text-center mt-2">
                  Konsultasi baru dan penebusan resep tidak dapat dilakukan. Riwayat dan dokumen kesehatan Kamu tetap dapat dilihat.
                </p>
              </div>
            )}
          </div>

          {/* Bagian Kanan */}
          <div className="flex flex-row flex-wrap w-full md:mt-0 md:w-1/2">
          
          {/* Livechat */}
          <div className="flex w-1/2 py-12 md:py-0 flex-col bg-morning-200 justify-center text-center">
            {!practitioner_role_id && !homeData?.teleconsult?.stage ? (
              <>
                <DoctorBusy isOpen={isModalOpen} onClose={() => setModalOpen(false)} />
                <div
                  className="loader_overlay cursor-pointer"
                  onClick={() => setModalOpen(true)}
                >
                  <div className="mb-3">
                    <img
                      className="w-auto h-16 mx-auto lazy"
                      src="https://m.linksehat.com/assets/img/icons/ask-doctor-white.png"
                      alt="Konsultasi Sekarang"
                    />
                  </div>
                  <h2 className="text-sm font-medium text-white sm:text-lg md:text-sm">
                    Livechat
                  </h2>
                </div>
              </>
            ) : (
              <div
                id="liveChatLink"
                className="loader_overlay cursor-pointer"
                onClick={handleOnClickLivechat}
              >
                <div className="mb-3">
                  <img
                    className="w-auto h-16 mx-auto lazy"
                    src="https://m.linksehat.com/assets/img/icons/ask-doctor-white.png"
                    alt="Konsultasi Sekarang"
                  />
                </div>
                <h2 className="text-sm font-medium text-white sm:text-lg md:text-sm">
                  Livechat
                </h2>
              </div>
            )}
          </div>


          {/* E-Prescription */}
            <div className="relative flex w-1/2 py-12 md:py-0 flex-col bg-morning justify-center text-center">
              <div
                id="ePrescriptionLink"
                className="loader_overlay cursor-pointer"
                onClick={() => navigate('/e-prescription')}
              >
                <div className="mb-3">
                  <img
                    className="w-auto h-16 mx-auto lazy"
                    src="https://m.linksehat.com/assets/img/icons/article-white.png"
                    alt="Info Kesehatan"
                  />
                  {homeData && homeData.prescription_count > 0 ? (
                    <div id="nilai_total_belum_download">
                      <span className="absolute top-2 right-2 block w-5 h-5 bg-red-600 text-white text-xs rounded-full flex items-center justify-center -mt-2 -mr-2">
                        {homeData.prescription_count}
                      </span>
                    </div>
                  ) : null  }
                  
                </div>
                <h2 className="text-sm font-medium text-white sm:text-lg md:text-sm">
                  E-Prescription
                </h2>

              </div>
            </div>

          {/* Auto */}
           {/* <div className="relative flex w-1/2 py-12 md:py-0 flex-col bg-morning justify-center text-center">
            <div
              className="loader_overlay cursor-pointer"
              onClick={() => navigate('/disclaimer/auto')}
            >
              <div className="mb-3">
                <img
                  className="w-auto h-16 mx-auto lazy"
                  src="https://m.linksehat.com/assets/img/icons/find-doctor-white.png"
                  alt="Auto Teleconsultation"
                />
              </div>
              <h2 className="text-sm font-medium text-white sm:text-lg md:text-sm">
                Auto Konsultasi
              </h2>
            </div>
          </div> */}
          </div>
        </div>

      {/* Notification Section */}

    <div className="max-w-md mx-auto px-4 pt-6 mb-2 bg-gray-100">
      {/* Consultation Schedule Card */}
      {homeData?.teleconsult && ['ongoing', 'upcoming'].includes(homeData.teleconsult.status) && (() => {
        const teleconsult = homeData.teleconsult;
        const dateObj = new Date(teleconsult.date_consultation);
        const day = String(dateObj.getDate()).padStart(2, '0');
        const month = dateObj.toLocaleString('id-ID', { month: 'long' });
        const dayName = dateObj.toLocaleString('id-ID', { weekday: 'long' });
        const formattedDate = `${dayName}, ${day} ${month} ${dateObj.getFullYear()}`;
        const doctorName = teleconsult.doctor_name || 'Dokter';
        const photoUrl = teleconsult.doctor_avatar_url || 'https://i.pravatar.cc/100?img=2';

        const handleClick = () => {
          if (teleconsult.status === 'upcoming') {
            setShowModalAppointment(true);
            return;
          }
          openConsultation(teleconsult);
        };

        return (
          <div onClick={handleClick} style={{ cursor: teleconsult.status === 'ongoing' ? 'pointer' : 'default' }}>
            <ConsultationCard
              doctorName={`${doctorName}`}
              date={formattedDate}
              time={teleconsult.time_consultation}
              photoUrl={photoUrl}
              startTime={teleconsult.start_time}
              duration={teleconsult.duration}
              serverTime={teleconsult.server_time}
            />
          </div>
        );
      })()}

      {pendingPayment && (
        <PendingPaymentCard
          pending={pendingPayment}
          onContinue={continuePendingPayment}
          onCancel={() => setShowCancelConfirm(true)}
          cancelling={cancellingPayment}
          onExpired={() => refetchHome()}
        />
      )}

      {homeData?.teleconsult && ['initial', 'final-confirm'].includes(homeData.teleconsult.stage) && (
        <ConsultationActionCard
          stage={homeData.teleconsult.stage}
          doctorName={homeData.teleconsult.doctor_name || 'Dokter'}
          doctorPhotoUrl={homeData.teleconsult.doctor_avatar_url}
          price={homeData.teleconsult.total_price ?? homeData.teleconsult.price}
          paymentDeadline={homeData.teleconsult.payment_deadline}
          acceptanceDeadline={homeData.teleconsult.acceptance_deadline}
          serverTime={homeData.teleconsult.server_time}
          paymentVerifying={homeData.teleconsult.payment_verifying}
          onAction={() => openConsultation(homeData.teleconsult)}
        />
      )}

      {/* Tanya Dokter Kami */}
        <div className="flex items-center justify-between  mb-2">
          <h3 className="text-md font-semibold mt-4 mb-2">Tanya Dokter Kami</h3>
          <button className="text-morning-300 text-sm font-medium" onClick={() => withPendingPaymentCheck(() => navigate('/list-doctor'))}>Lihat Semua</button>
        </div>
        {isLoading ? 
        <div className="flex-1 flex items-center justify-center">
          <Loading />
        </div>
        : 
        doctorData.map((doc, index) => (
          <div key={index} className="mb-4">
            <DoctorCard 
              doctor={doc} 
              isDokterUmum={isDokterUmum}
              onConsult={() => withPendingPaymentCheck(() => {
                setSelectedDoctor(doc);
                setShowService(true);
              })}
            />
          </div>
        ))}

      {/* Specialist */}
      {/* <div className="max-w-md mx-auto px-1 py-4 bg-gray-100">
        <h3 className="text-md font-semibold mb-4">Spesialis</h3>
        {isLoading ?
        <div className="flex-1 flex items-center justify-center">
          <Loading />
        </div>
        :
        <div className="grid grid-cols-3 gap-4">
          {specialistData.map((item, index) => (
            <SpecialistCard 
              key={index} 
              name={item.name} 
              iconUrl={item.iconUrl} 
              onClick={() => handleSpecialistClick(item.id)}
            />
          ))}
        </div>
        }
      </div> */}
      
      </div>


      {/* MODALS */}
      <PendingPaymentModal
        pending={pendingAction ? pendingPayment : null}
        onContinue={continuePendingPayment}
        onCancelAndNew={() => cancelPayment(pendingAction ?? undefined)}
        onClose={() => setPendingAction(null)}
        loading={cancellingPayment}
      />

      <Confirm
        show={showCancelConfirm}
        title="Batalkan Pesanan?"
        message="Pesanan konsultasi yang belum dibayar akan dibatalkan dan slot dokter dilepas. Anda dapat membuat pesanan baru setelahnya."
        confirmText="Batalkan"
        cancelText="Kembali"
        onConfirm={() => cancelPayment()}
        onCancel={() => setShowCancelConfirm(false)}
        loading={cancellingPayment}
      />

      <Notification
        show={showModal}
        onClose={() => setShowModal(false)}
        message="Mohon maaf untuk saat ini livechat dibatasi sehari sekali"
      />

      <Notification
        show={showModalAppointment}
        onClose={() => setShowModalAppointment(false)}
        message="Konsultasi akan dimulai pada waktu yang telah dijadwalkan. Silakan tunggu hingga waktu tersebut"
      />


      <Service
        isOpen={showService}
        onClose={() => setShowService(false)}
        onSubmit={(data) => {
          setShowService(false);
          navigate(`/disclaimer/${selectedDoctor?.practitioner_role_id}`, {
            state: {
              serviceType: data.serviceType,
            },
          });
        }}
        prices={{
          chat: selectedDoctor?.price_chat,
          video: selectedDoctor?.price_video
        }}
        isOnline={selectedDoctor?.is_online || false}
      />

      <InfoModal
        show={showActiveConsultation && isActiveConsultation(homeData?.teleconsult)}
        title="Konsultasi Masih Berjalan"
        message={isActiveConsultation(homeData?.teleconsult) ? activeConsultationMessage(homeData.teleconsult) : ''}
        actionLabel="Lihat Konsultasi"
        secondaryLabel="Tutup"
        onSecondary={() => setShowActiveConsultation(false)}
        onClose={() => {
          setShowActiveConsultation(false);
          if (isActiveConsultation(homeData?.teleconsult)) {
            navigate(activeConsultationRoute(homeData.teleconsult));
          }
        }}
      />

      <InfoModal
        show={!!insuranceNotice}
        title="Masa Asuransi Tidak Aktif"
        message={`${insuranceNotice || ''}. Silakan hubungi BRI Life untuk informasi kepesertaan Anda.`}
        onClose={() => setInsuranceNotice(null)}
      />

      <InfoModal
        show={!!errorMessage}
        title="Terjadi Kesalahan"
        message={errorMessage || ''}
        onClose={() => setErrorMessage(null)}
      />

      {/* Bottom Navigation */}
      <BottomNav />
    </div>
  );
};

export default Home;
